import { createContext, useCallback, useContext } from 'react';

import { Permission, type EnforcedPlanFeature, type PlanFeatureKey } from '@bookora/shared';
import {
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import { queryKeys } from '@/api/query-keys';
import { useAuth } from '@/auth/token-store';
import { planLockReason } from '@/lib/reasons';
import { useCan } from '@/permissions/use-can';

import { fetchPlanAccess } from './plan-access-api';
import { UNKNOWN_PLAN_NAME } from './plan-lock-copy';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  PLAN ACCESS — what the lock family reads, and how it fails
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `GET /billing/plan-access` (C4) answers for ANY staff member (a receptionist
 * needs the badge and «به مدیر کلینیک بگید» as much as the owner needs the
 * upgrade link). Shape per CONTRACTS-PRICING §4.1; `enforced: false` → no
 * locks and every feature open: a clinic with no live subscription is never
 * plan-gated (it is read-only for billing reasons instead, which the billing
 * banner explains).
 *
 * ── THE LOCK UI FAILS OPEN ──────────────────────────────────────────────────
 *
 * While the read is loading, or when it failed, every feature is `open`. The
 * server is the gate (`PlanFeatureInterceptor` refuses a gated write with 402
 * FEATURE_NOT_IN_PLAN, and `PlanRefusalWatcher` explains it); the panel only
 * explains. A lock drawn from a guess would hide a paying clinic's screen
 * behind a network hiccup. Tours are the opposite: they fail CLOSED
 * (`guidance/features.ts`), because a tour pointing at a locked tab is worse
 * than no tour.
 *
 * ── LOCKED vs READ-ONLY ─────────────────────────────────────────────────────
 *
 * A lock whose `hasData` is true is `read-only`: the clinic created records
 * of it on a higher plan (or in a trial) and keeps reading and exporting them
 * after a downgrade (owner D6); only new writes are closed. `locked` is a
 * feature the clinic never used — its screen shows the pitch instead.
 */

export interface PlanAccessLock {
  feature: EnforcedPlanFeature;
  labelFa: string;
  requiredPlanKey: string | null;
  requiredPlanName: string | null;
  /** The clinic already holds records of it → read-only, not a blank lock. */
  hasData: boolean;
}

export interface PlanAccessResponse {
  planKey: string | null;
  planName: string | null;
  billingModel: 'FLAT' | 'RAAS' | null;
  status: string | null;
  trialing: boolean;
  trialEndsAt: string | null;
  enforced: boolean;
  features: Partial<Record<PlanFeatureKey, boolean>>;
  locks: PlanAccessLock[];
}

export type PlanLockStateKind = 'open' | 'locked' | 'read-only';

export interface PlanLock {
  state: PlanLockStateKind;
  requiredPlanKey: string | null;
  requiredPlanName: string | null;
  currentPlanName: string | null;
  /** No live subscription at all. */
  noPlan: boolean;
  trialing: boolean;
  /** The clinic is on «نتیجه‌محور». */
  raas: boolean;
}

const OPEN: PlanLock = Object.freeze({
  state: 'open',
  requiredPlanKey: null,
  requiredPlanName: null,
  currentPlanName: null,
  noPlan: false,
  trialing: false,
  raas: false,
});

/** The server's cache is 60 s; the panel's matches it. */
export const PLAN_ACCESS_STALE_MS = 60_000;

/**
 * The lock of one feature, from a plan-access answer — pure, so the rule is
 * tested once and every hook below is a thin read of it.
 *
 * `undefined` (loading, failed) and `enforced: false` are both OPEN. A
 * feature with no `locks[]` entry is open whatever `features` says: `locks`
 * is the server's list of what it refuses, and the panel draws no lock the
 * server would not enforce.
 */
export function planLockOf(
  access: PlanAccessResponse | undefined,
  feature: EnforcedPlanFeature,
): PlanLock {
  if (!access || !access.enforced) return OPEN;
  const lock = access.locks.find((entry) => entry.feature === feature);
  const context = {
    currentPlanName: access.planName,
    noPlan: access.planKey === null,
    trialing: access.trialing,
    raas: access.billingModel === 'RAAS',
  };
  if (!lock) {
    return { ...OPEN, ...context };
  }
  return {
    state: lock.hasData ? 'read-only' : 'locked',
    requiredPlanKey: lock.requiredPlanKey,
    requiredPlanName: lock.requiredPlanName,
    ...context,
  };
}

/** Whether a lock stops new writes (locked or read-only). */
export function isPlanLocked(lock: PlanLock): boolean {
  return lock.state !== 'open';
}

/**
 * The plan-access query (`queryKeys.billing.planAccess(clinicId)`, staleTime
 * 60 s, retry false). Every lock hook reads this one query, so a sidebar, a
 * tab strip and a create button never disagree.
 */
export function usePlanAccess(): UseQueryResult<PlanAccessResponse> {
  const { activeClinicId } = useAuth();
  return useQuery({
    queryKey: queryKeys.billing.planAccess(activeClinicId),
    queryFn: ({ signal }) => fetchPlanAccess(signal),
    enabled: Boolean(activeClinicId),
    staleTime: PLAN_ACCESS_STALE_MS,
    retry: false,
  });
}

/**
 * Whether plan access has answered (or failed) — for a deep link that opens a
 * WRITE form (`?compose=`, `?edit=`, `?activate=`, `?new=1`): it waits for
 * the answer rather than opening the form on the fail-open guess and closing
 * it a moment later. A failed read counts as settled: the server is the gate.
 */
export function usePlanAccessSettled(): boolean {
  return !usePlanAccess().isLoading;
}

/** The lock of one enforced feature. Fails open while loading or on error. */
export function usePlanLock(feature: EnforcedPlanFeature): PlanLock {
  const { data } = usePlanAccess();
  return planLockOf(data, feature);
}

/**
 * The blocked-action reason for `feature` — `null` when it is open — in the
 * words of `planLockReason()`: the billing holder is told where to upgrade,
 * everyone else whom to tell. For a create button OUTSIDE a
 * `RequirePlanFeature` (the accounting header's «ثبت هزینه»); inside one,
 * `usePlanLockReason()` reads the same sentence from context.
 */
export function usePlanLockReasonFor(feature: EnforcedPlanFeature): string | null {
  const lock = usePlanLock(feature);
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  if (!isPlanLocked(lock)) return null;
  return planLockReason(lock.requiredPlanName ?? UNKNOWN_PLAN_NAME, canBilling);
}

/**
 * The blocked-action reason inside a read-only section («این کار در پلن … است.
 * …»), for `disabledReason`. `RequirePlanFeature` provides it; `null` (open)
 * everywhere else.
 */
export const PlanLockContext = createContext<string | null>(null);

export function usePlanLockReason(): string | null {
  return useContext(PlanLockContext);
}

/** Drops the cached plan access of `clinicId`, so the next read asks the server. */
export function invalidatePlanAccess(
  queryClient: QueryClient,
  clinicId: string | null,
): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: queryKeys.billing.planAccess(clinicId) });
}

/**
 * For a mutation's `onSuccess` that changes the plan (subscribe, cancel,
 * resume, scheduled-cancel, trial-plan switch). `PlanRefusalWatcher` already
 * refetches whenever the billing state changes, so a caller that invalidates
 * the billing state is covered without this; it is here for one that does not.
 */
export function useInvalidatePlanAccess(): () => Promise<void> {
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();
  return useCallback(
    () => invalidatePlanAccess(queryClient, activeClinicId),
    [queryClient, activeClinicId],
  );
}
